import {
  Badge,
  ClickAwayListener,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
} from '@material-ui/core';
import { MoreHoriz } from '@material-ui/icons';
import { Color, ColorDark, ColorDynamic, Stack } from '@superdispatch/ui';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { SidebarLinkLegacy } from './AppSidebarMenuItem';

const StyledBadge = styled(Badge)`
  & .MuiBadge-badge {
    border: 2px solid ${Color.Dark500};
    top: 2px;
  }

  & .MuiBadge-dot {
    margin-top: 2px;
    margin-right: 2px;
    height: 16px;
    width: 16px;
    border-radius: 50%;
  }
`;

const MoreButton = styled.button`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 70px;
  margin-bottom: 4px;
  padding: 6px 8px;
  margin-left: 8px;
  margin-right: 8px;

  border-radius: 3px;
  border: none;
  background: transparent;

  color: ${Color.Silver500};
  font-size: 12px;
  letter-spacing: -0.5px;
  line-height: 16px;

  &:hover {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.2);
  }

  svg {
    width: 20px;
    height: 20px;
    path {
      fill: ${Color.Silver500};
    }
  }

  &:hover svg path {
    fill: ${Color.White};
  }
`;

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: 24px;
  margin-right: 12px;
  color: ${Color.Silver500};

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    fill: ${Color.Silver500};
  }
`;

const StyledListItemText = styled(ListItemText)`
  margin: 0;

  & .MuiListItemText-primary {
    color: ${Color.Silver500};
    font-size: 12px;
    letter-spacing: -0.5px;
    line-height: 16px;
  }
`;

const StyledPaper = styled(Paper)`
  background-color: ${({ theme }) =>
    theme.palette.type === 'dark' ? ColorDark.White : Color.Dark500};
  border: 1px solid ${ColorDynamic.Dark500};
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  padding: 8px;
  z-index: 500;
`;

const HoverContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledMenuItem = styled(MenuItem)`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  min-width: 200px;
  border-radius: 3px;
  color: ${Color.Silver500};

  &:hover {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.2);

    ${StyledListItemIcon} path {
      fill: ${Color.White};
    }

    ${StyledListItemText} .MuiListItemText-primary {
      color: ${Color.White};
    }
  }

  &:focus {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.2);

    ${StyledListItemIcon} path {
      fill: ${Color.White};
    }

    ${StyledListItemText} .MuiListItemText-primary {
      color: ${Color.White};
    }
  }

  &[aria-current='page'],
  &.active {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.5);

    ${StyledListItemIcon} path {
      fill: ${Color.White};
    }

    ${StyledListItemText} .MuiListItemText-primary {
      color: ${Color.White};
    }
  }

  &.Mui-disabled {
    opacity: 0.5;
  }
`;

const StyledPopper = styled(Popper)`
  z-index: 2000;
  margin-left: 8px;
`;

function renderMenuItemContent(link: SidebarLinkLegacy): ReactElement {
  return (
    <>
      <StyledListItemIcon>
        <StyledBadge
          badgeContent={link.badgeContent}
          color="error"
          overlap="rectangular"
          max={999}
          variant={link.badgeType}
          invisible={!link.badgeContent}
        >
          {link.Icon && <link.Icon />}
        </StyledBadge>
      </StyledListItemIcon>
      <StyledListItemText primary={link.label} />
    </>
  );
}

interface SidebarMoreMenuProps {
  links: SidebarLinkLegacy[];
}

const CLOSE_DELAY = 500;

export function AppSidebarMoreMenu({ links }: SidebarMoreMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const handleMenuClose = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(false);
  }, [clearCloseTimeout]);

  const handleMouseLeave = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, CLOSE_DELAY);
  }, [clearCloseTimeout]);

  if (links.length === 0) return null;

  const renderMenuItem = (link: SidebarLinkLegacy) => {
    const { RenderComponent } = link;
    if (RenderComponent) {
      return <RenderComponent key={link.key} link={link} />;
    }

    const baseProps = {
      key: link.key,
      onClick: () => {
        handleMenuClose();
        return link.onClick ? () => link.onClick?.() : undefined;
      },
      disabled: link.disabledLink,
      dense: true,
    };

    const content = renderMenuItemContent(link);

    if (link.localPath) {
      return (
        <StyledMenuItem
          {...baseProps}
          // @ts-expect-error - styled-components doesn't preserve MUI's polymorphic component prop
          component={NavLink}
          to={link.localPath}
          disableRipple={true}
        >
          {content}
        </StyledMenuItem>
      );
    }

    if (link.href) {
      return (
        <StyledMenuItem
          {...baseProps}
          // @ts-expect-error - styled-components doesn't preserve MUI's polymorphic component prop
          component="a"
          href={link.href}
          target={link.target || '_self'}
          rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {content}
        </StyledMenuItem>
      );
    }

    return <StyledMenuItem {...baseProps}>{content}</StyledMenuItem>;
  };

  return (
    <HoverContainer
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') handleOpen();
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') handleMouseLeave();
      }}
    >
      <MoreButton
        ref={buttonRef}
        aria-label="More menu items"
        data-intercom-target="sidebar-more-button"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <MoreHoriz />
        More
      </MoreButton>
      <StyledPopper
        open={isOpen}
        anchorEl={buttonRef.current}
        placement="right-end"
        transition={true}
        modifiers={{
          offset: { enabled: true, offset: '0, 4' },
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleMenuClose}>
            <Grow {...TransitionProps}>
              <StyledPaper>
                <Stack space="xxsmall">
                  {links.map((link) => renderMenuItem(link))}
                </Stack>
              </StyledPaper>
            </Grow>
          </ClickAwayListener>
        )}
      </StyledPopper>
    </HoverContainer>
  );
}
